/**
 * BFF (Back-for-Frontend) List Response Format
 * Standardizes list responses across admin facade
 */

export type BffListResult<T> = {
  data: T[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
};

/**
 * Creates a standardized BFF list result
 * @param rows - Array of data items
 * @param total - Total count of items for the query
 * @param limit - Page size limit
 * @param offset - Page offset (default 0)
 * @returns BFF formatted response
 */
export function toBffList<T>(rows: T[], total: number, limit: number, offset = 0): BffListResult<T> {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeTotal = Number.isFinite(total as number) ? (total as number) : safeRows.length;
  const safeLimit = Math.max(0, limit | 0);
  const safeOffset = Math.max(0, offset | 0);
  
  return {
    data: safeRows,
    total: safeTotal,
    limit: safeLimit,
    offset: safeOffset,
    has_more: safeOffset + safeRows.length < safeTotal,
  };
}

/**
 * Type guard to check if response is BFF formatted
 */
export function isBffListResult<T>(value: any): value is BffListResult<T> {
  return value && 
    Array.isArray(value.data) &&
    typeof value.total === 'number' &&
    typeof value.limit === 'number' &&
    typeof value.offset === 'number' &&
    typeof value.has_more === 'boolean';
}